import {
	Channel,
	ChatMessageFormat,
	ContactLeg,
	CreateReason,
	Event,
	EventBus,
	EventBusInstance,
	LegState,
	ConnectionInstance,
	ConnectionState
} from "@sap/cpaas-commlib-lib";
import {Component} from "inferno";
import {App, Views} from "../../App";
import {AppState, Configuration} from "../../services/configuration";
import {MessageProvider, MessageType} from "../../services/messageProvider";
import {Bundle} from "../../services/translationProvider";
import {Entity, IChatMessage, Level, SystemMessageChatState} from "../../utils/types";
import {guid, resizeApp} from "../../utils/util";
import {Footer} from "./Footer";
import {Header} from "./Header";
import {Input as ChatInput} from "./input/Input";
import {Transcript} from "./transcript/Transcript";
import {Video} from "./Video";
import {IFile, MediaDrop} from "../../components/mediadrop/MediaDrop";
import {Logger} from "../../services/logger";
import {CommlibService} from "../../services/commlibService";

export type Props = { toView: (view: Views) => void };
export type State = { agentName: string, minimized: boolean, messages: IChatMessage[], minimizedMessageCount: number, chatActive: boolean, chatInQueue: boolean, notificationActive: boolean };

export class Chat extends Component<Props, State> {
	state: State = {
		minimized: false,
		messages: [],
		minimizedMessageCount: 0,
		chatActive: false,
		chatInQueue: true,
		notificationActive: true,
		agentName: "",
	};
	private header: HTMLElement;
	private lastReceived: {entity: Entity, previousPrintedTime: Date};
	private input: any;
	private video: any;
	private drop: any;
	private participants: string[] = [];
	private audioElement: any;
	private completed: boolean = false;
	private broadcastChannel: BroadcastChannel;
	private chatWillCloseByOtherTabEvent = "chatWillCloseByOtherTab";
	private chatClosedByOtherTabEvent = "chatClosedByOtherTab";
	private chatViewTabSyncChannelName = "chatViewTabSync";

	constructor(props: any) {
		super(props);
		const timestamp: IChatMessage = {
			guid: guid(),
			entity: Entity.notification,
			level: Level.Info,
			timestamp: new Date().toLocaleString(AppState.language, {hour: "numeric", minute: "numeric"})
		};
		if (Configuration.incoming) {
			Configuration.incoming = undefined;
			this.state.chatActive = true;
			if (AppState.activeLeg) {
				this.state.chatInQueue = AppState.activeLeg.getState() === LegState.proceeding;
				const agent = AppState.activeLeg.getContact().getLegs().find(l => l.getReason() === CreateReason.Offer);
				this.state.agentName = agent?.getAddress() || "";
			}
		} else {
			const legState = AppState.activeLeg.getState();
			this.state.chatActive = (legState === LegState.proceeding || legState === LegState.connected);
		}
		this.state.messages = [
			timestamp,
			{
				guid: guid(),
				entity: Entity.notification,
				level: Level.Info,
				content: Bundle.t("VCC_XMSG_CHAT_STARTING")
			}				
		];
		AppState.messages = [];
		if (typeof window.BroadcastChannel === "function") {
			this.broadcastChannel = new BroadcastChannel(this.chatViewTabSyncChannelName);
		}
	}

	componentDidMount() {
		EventBusInstance.subscribe(Event.Chat(Channel.all).message, this.onChatMessage, this);
		EventBusInstance.subscribe(Event.ContactLeg(Channel.all).change, this.onChatStateChange, this);
		EventBusInstance.subscribe(Event.ContactLeg(Channel.all).new, this.onChatStateChange, this);
		EventBusInstance.subscribe(Event.ContactLeg(Channel.all).incoming, this.onChatLegChange, this);
		EventBusInstance.subscribe(Event.OtherContactLeg(Channel.all).connected, this.onLegConnected, this);
		EventBusInstance.subscribe(Event.OtherContactLeg(Channel.all).disconnected, this.onLegDisconnected, this);
		EventBus.subscribe(Configuration.minimizeEvent, this.onMinimizeEvent, this, Configuration.component);
		resizeApp(Configuration.maxWidth, Configuration.maxHeight);
		if (Configuration.soundNotification && !Configuration.notificationFile) { 
			Configuration.notificationFile = 'alert_high-intensity.wav'; //CC365V1-6638
		}
		if (Configuration.soundNotification && Configuration.notificationFile) {
			this.audioElement = new Audio('resources/sounds/' + Configuration.notificationFile);
		}
		if (this.broadcastChannel) {
			this.broadcastChannel.onmessage = this.onMessageFromOtherTab;
		}
	}

	componentWillUnmount() {
		EventBusInstance.unsubscribe(Event.Chat(Channel.all).message, this.onChatMessage, this);
		EventBusInstance.unsubscribe(Event.ContactLeg(Channel.all).change, this.onChatStateChange, this);
		EventBusInstance.unsubscribe(Event.ContactLeg(Channel.all).new, this.onChatStateChange, this);
		EventBusInstance.unsubscribe(Event.ContactLeg(Channel.all).incoming, this.onChatLegChange, this);
		EventBusInstance.unsubscribe(Event.OtherContactLeg(Channel.all).connected, this.onLegConnected, this);
		EventBusInstance.unsubscribe(Event.OtherContactLeg(Channel.all).disconnected, this.onLegDisconnected, this);
		EventBus.unsubscribe(Configuration.minimizeEvent, this.onMinimizeEvent, this, Configuration.component);
	}

	onClose = async () => {
		// If minimized, expand to allow user to see the dialog.
		if (this.state.minimized) {
			Logger.debug('Chat:onClose - Expand to show confirmation dialog');
			this.onMinimize(null);
		}
		// Always ask confirmation from user to permanently close chat conversation.
		try {
			Logger.info('Chat:onClose - Asking user to confirm closing conversation');
			await App.ShowQuery(Bundle.t("VCC_XBUT_CHAT_BUTTON_END_AND_CLOSE") + "?", Level.Info, "VCC_XBUT_BTN_OK", "VCC_XBUT_BTN_CANCEL")
		} catch (e) {
			Logger.info('Chat:onClose - User canceled');
			return;
		}
		// End session.
		Logger.debug('Chat:onEndSession - Ending session due user closing chat, closing frame');
		this.broadcastChannel?.postMessage(this.chatWillCloseByOtherTabEvent);
		this.onEndSession();
		MessageProvider.sendMessage(MessageType.state, false); // CVC hides iframe and restores chat notification button.
		this.props.toView(Views.empty);	
		this.broadcastChannel?.postMessage(this.chatClosedByOtherTabEvent);
	}

	onMessageFromOtherTab = (event:MessageEvent<any>) => {
		/*
		 AppState.disconnecting flag is raised by Login.tsx and Chat.tsx when websocket is being closed intentionally.
		 Flag value is synched between tabs using Broadcast Channel.
		 This is to ensure that all existing tabs are aware of websocket being closed intentionally.
		*/
		if (event.data === this.chatWillCloseByOtherTabEvent) {
			Logger.debug('Chat:onMessageFromOtherTab - Chat & ws will be closed from other tab, as used closed frame');
			AppState.disconnecting = true; // Raise flag to treat ws disconnection as intentional on this tab
		}
		if (event.data === this.chatClosedByOtherTabEvent) {
			Logger.trace('Chat:onMessageFromOtherTab - User closed chat on other browser tab -> closing now on this tab');
			this.props.toView(Views.empty);
			MessageProvider.sendMessage(MessageType.state, false); // CVC hides iframe and restores chat notification button.
		}
	};

	onEndChat = async () => {
		try {
			await App.ShowQuery(Bundle.t("VCC_XBUT_CHAT_BUTTON_END") + "?", Level.Info, "VCC_XBUT_BTN_OK", "VCC_XBUT_BTN_CANCEL")
		} catch (e) {
			return;
		}
		try {
			if (this.video && this.video.active) {
				this.video.stopVideo();
			}
			await AppState.activeLeg.handle();
		} catch (e) {
			App.ShowMsg("VCC_XMSG_ALERT_FAIL_END", Level.Warning);
		}
	};

	onMinimizeEvent = () => {
		this.onMinimize(null);
	};

	onMinimize = (event: MouseEvent) => {
		const newState = !this.state.minimized;
		MessageProvider.sendMessage(MessageType.minimized, newState);
		if (newState) {
			this.video.stopVideo();
			resizeApp(Configuration.maxWidth, this.header.offsetHeight, true);
		} else {
			resizeApp(Configuration.maxWidth, Configuration.maxHeight);
			this.setState({minimizedMessageCount: 0});
		}
		this.setState({minimized: newState});
	};

	onEndSession = async () => {
		if (this.video && this.video.active) {
			Logger.debug('Chat:onEndSession - Stopping video');
			this.video.stopVideo();
		}
		if (this.state.chatActive) {
			Logger.debug('Chat:onEndSession - Ending active chat leg');
			await AppState.activeLeg?.handle().catch((e) => Logger.debug("Chat:onClose failed", e.message));
		}
		// disconnect WS after 1 sec delay (allow chat legs to end cleanly, before disconnecting)
		if (ConnectionInstance.getConnectionState() === ConnectionState.Connected) {
			Logger.debug('Chat:onEndSession - Active websocket will be closed');
			AppState.disconnecting = true;
			setTimeout(() => {
				Logger.debug('Chat:onEndSession - Closing active websocket now');
				CommlibService.disconnect().catch(()=>{
					Logger.debug('Chat:onEndSession - Closing websocket failed - Most likely was already closed');
				});
			},1000);
		} else {
			Logger.debug('Chat:onEndSession - No active websocket to close');
		}
	};

	private isHuman(reason: CreateReason) {
		return reason !== CreateReason.Service && reason !== CreateReason.Queue && reason !== CreateReason.From;
	}

	private onChatLegChange() {
		this.setState({
			chatActive: true,
			messages: [{
				guid: guid(),
				entity: Entity.notification,
				level: Level.Info,
				timestamp: new Date().toLocaleString(AppState.language, {hour: "numeric", minute: "numeric"})
			}]
		});
	}

	private onChatStateChange(channel: string, event: string, contactLeg: ContactLeg) {
		const state = contactLeg.getState();
		let messages = [...this.state.messages];
		if (this.completed) {
			// chat was completed => clear everything
			messages = [];
			this.completed = false;
		}
		switch (state) {
			case LegState.connected:
				for (const leg of contactLeg.getContact().getLegs()) {
					const address = leg.getAddress() || Bundle.t("VCC_XMSG_ALIAS_UNKNOWN");
					if (this.isHuman(leg.getReason()) && this.participants.indexOf(address) === -1) {
						this.participants.push(address);
						this.setState({agentName: address});
					}
				}
				break;
			case LegState.proceeding:
				break;
			case LegState.completed:
			case LegState.done:
				messages.push({
					guid: guid(),
					entity: Entity.notification,
					level: Level.Info,
					content: Bundle.t("VCC_XMSG_CHAT_ENDED")
				});
				this.completed = true;
				break;
			default:
				break;
		}
		const chatActive = (state !== LegState.disconnected && state !== LegState.completed && state !== LegState.done);
		if (!chatActive) {
			Logger.debug('Chat:onChatStateChange - Leg state is [' + state + '], chat has ended on server. Ending chat session on client-side');
			this.onEndSession();
		}
		this.setState({chatActive, messages, chatInQueue: state === LegState.proceeding});
	}

	private onLegConnected(channel: string, event: string, contactLeg: ContactLeg) {
		const address = contactLeg.getAddress() || Bundle.t("VCC_XMSG_ALIAS_UNKNOWN");
		if (this.isHuman(contactLeg.getReason()) && !this.state.chatInQueue && this.participants.indexOf(address) !== -1) {
			this.participants.push(address);
			const messages = [...this.state.messages, {
				guid: guid(),
				entity: Entity.notification,
				level: Level.Primary,
				content: (contactLeg.getAddress() || Bundle.t("VCC_XMSG_ALIAS_UNKNOWN")) + " " + Bundle.t("VCC_XMSG_CHAT_JOIN"),
			}];
			this.setState({messages, agentName: address});
		}
	}

	private onLegDisconnected(channel: string, event: string, contactLeg: ContactLeg) {
		if (this.isHuman(contactLeg.getReason()) && !this.state.chatInQueue) {
			const address = contactLeg.getAddress() || Bundle.t("VCC_XMSG_ALIAS_UNKNOWN");
			const index = this.participants.indexOf(address);
			const count = AppState.activeLeg.getContact().getLegs().filter((leg) => leg.getAddress() === contactLeg.getAddress()).length;
			if (index !== -1 && count === 0) {
				// Last participant/agent left (visitor is not included in this.participants)
				this.participants.splice(index, 1);
			}
		}
	}

	private onChatMessage(channel: string, event: string, message: IChatMessage) {
		const messages = [...this.state.messages];
		if (message.sender === -1) {
			Logger.debug('Chat:onChatMessage - Ignoring own message echoed by EcfAdapter: ', JSON.stringify(message))
			return;
		}
		message = this.formatMessage(message);
		if (message === null) {
			Logger.debug('Chat:onChatMessage - Ignoring system message: ', JSON.stringify(message))
			return;
		}		
		AppState.messages.push(message);
		MessageProvider.sendMessage(MessageType.event, {
			event: "message",
			carry: {content: message.content, alias: message.alias, time: message.time}
		});
		if (message.format === ChatMessageFormat.template && message.content?.template?.template_type === "response") {
			// look in the transcript for matching template request
			for (const chatMessage of messages) {
				const template = chatMessage.content?.template;
				if (chatMessage.format === ChatMessageFormat.template && (template.template_type === "static_request" || template.template_type === "request") && template.id === message.content.template.id) {
					// there is a match => convert request message to response
					const messageCopy = {...chatMessage};
					messageCopy.content.template = message.content.template;
					messageCopy.entity = Entity.visitor;
					messages[messages.indexOf(chatMessage)] = messageCopy;
					// hide the response message
					message.hidden = true;
					break;
				}
			}
		}

		messages.push(message);

		if (!this.state.minimized) {
			this.setState({messages, minimizedMessageCount: 0});
		} else {
			const minimizedMessageCount = this.state.minimizedMessageCount + 1;
			this.setState({messages, minimizedMessageCount});
		}

		if (Configuration.soundNotification && this.state.notificationActive) {
			if (message.entity === Entity.agent && message.hidden !== true && Configuration.soundNotification && Configuration.notificationFile) {
				this.audioElement.play();
			}
		}
	}

	/**
	 * Add missing metadata to the message
	 */
	private formatMessage(message: IChatMessage): IChatMessage {
		message.guid = guid();
		if (AppState.activeLeg.getId() === message.sender) {
			message.entity = Entity.visitor;
			message.alias = Configuration.userName || "visitor";
		} else {
			const leg = AppState.activeLeg.getContact().getLegById(message.sender);
			if (leg) {
				message.entity = leg.getReason() !== CreateReason.Queue ? Entity.agent : Entity.system;
				message.alias = leg.getAddress() || "";
			} else {
				message.entity = Entity.unknown;
				message.alias = "";
			}
		}
		let time: Date;
		try {
			if (message.time.indexOf("/") !== -1) {
				// Timestamp was constructed by Chat Server, and it is in dd/mm/yyyy format, e.g. 10/11/2022 16:23:16 UTC+00:00 -> 10th of November.
				// Javascript Date constructor however requires mm/dd/yyyy. Let's convert.
				let split = message.time.split("/");
				message.time = split[1] + "/" + split[0] + "/" + split[2]; // -> e.g. 11/10/2022 16:23:16 UTC+00:00
			}
			// Note: Timestamps constructed by CVC itself are ISO8601, for example 2022-11-10T17:48:20.333Z. These don't require conversion.
			time = new Date(message.time);
		} catch (e) {
			time = new Date();
		}
		if(!this.lastReceived){
			// First message in chat shall always have timestamp.
			message.timestamp = time.toLocaleString(AppState.language, {hour: "numeric", minute: "numeric"});
			this.lastReceived = {entity:message.entity, previousPrintedTime: time};
		} else if (time.getMinutes() != this.lastReceived.previousPrintedTime.getMinutes() || message.entity !== this.lastReceived.entity) {
			// If message sender is different than sender of previous message, or if minute has changed, print timestamp.
			message.timestamp = time.toLocaleString(AppState.language, {hour: "numeric", minute: "numeric"});
			this.lastReceived.previousPrintedTime = time;
		}
		this.lastReceived.entity = message.entity;
		if (message.format === ChatMessageFormat.template) {
			// parse template content
			try {
				message.content = JSON.parse(message.content);
				// seems like server mangles/sanitizes HTML in a way that it needs to be encoded to survive, decode the response here
				message.content.template.response_html = message.content.template.response_html ? decodeURI(message.content.template.response_html) : undefined;
			} catch (e) {
				message.content = {response: "parseError"};
			}
		}

		if (message.format === ChatMessageFormat.state) { // CC365V1-3833
			const content = JSON.parse(message.content);
			let notificationContent = "";
			const state = content.state as SystemMessageChatState;
			switch (state) {
				case SystemMessageChatState.joined:
					const agentDisplayName = content.originator.display_name?.length > 0 ? content.originator.display_name : Bundle.t("VCC_XTIT_TRANSCRIPT_AGENT");
					notificationContent = Bundle.t("VCC_XMSG_CHAT_JOIN").replace('{participant}', agentDisplayName); // Join-messages are always for agent.
					message.level = Level.Primary;
					break;
				case SystemMessageChatState.transferred:
					notificationContent = Bundle.t("VCC_XMSG_CHAT_TRANSFER")
					message.level = Level.Warning;
					break;
				case SystemMessageChatState.left:
					if (content.originator.id === AppState.visitorUserId.toLowerCase()) {
						notificationContent = Bundle.t("VCC_XMSG_CHAT_VISITOR_LEAVE");
					}
					else {
						const agentDisplayName = content.originator.display_name?.length > 0 ? content.originator.display_name : Bundle.t("VCC_XTIT_TRANSCRIPT_AGENT");
						notificationContent = Bundle.t("VCC_XMSG_CHAT_LEAVE").replace('{participant}', agentDisplayName);						
					}
					message.level = Level.Primary;
					break;				
				default:
					Logger.debug('Chat:formatMessage - Skip formatting state ['+content.state+']', JSON.stringify(content));
					return null; // This 'status' message can be skipped in CVC
			}
			message.entity = Entity.notification;
			message.timestamp = undefined;
			message.content = notificationContent;
		}
		return message;
	}

	onAddText = (text: string) => {
		this.input.addText(text);
	};

	onAddAttachment = async (files: IFile[]) => {
		if (files.length === 0) {
			return;
		}

		for (const file of files) {
			App.ShowMsg(Bundle.t("VCC_XMSG_ATTACH_UPLOAD" + " " + file.name), Level.Info);
			const headers = {
				"Content-Disposition": "attachment; filename=\"" + file.name + "\"",
				"Content-Type": file.type,
			};
			try {
				const result = await fetch(Configuration.endpoint + "/attachment", {
					method: "POST",
					credentials: "include",
					body: file,
					headers
				});
				if (!result.ok) {
					throw new Error(result.statusText);
				}
				const attachmentId = await result.text();
				await AppState.activeLeg.sendMessage(attachmentId, ChatMessageFormat.attachment);
			} catch (e) {
				App.ShowMsg(Bundle.t("VCC_XMSG_ATTACH_UPLOAD_FAIL" + " " + file.name), Level.Danger);
			}
		}
	};

	onMenuAttach = (item: "file" | "video") => {
		switch (item) {
			case "video":
				this.video.startVideo();
				break;
			default:
				this.drop.open();
				break;
		}
	};

	onToggleNotification = () => {
		if (Configuration.soundNotification) {
			this.setState({notificationActive: !this.state.notificationActive});
		}
	};

	render() {
		return (
			<div className="h-100 d-flex flex-column chat-page" id="chat-root">
				<div ref={(node: HTMLElement) => this.header = node}>
					<Header showUser={!this.state.chatInQueue && this.state.chatActive}
							messageCount={this.state.minimizedMessageCount} minimized={this.state.minimized}
							onMinimize={this.onMinimize} onClose={this.onClose} alias={this.state.agentName} />
				</div>
				{!this.state.minimized && ([
					<Video ref={(ref: any) => this.video = ref} />,
					<Transcript messages={this.state.messages} />,
					<ChatInput ref={(ref: any) => this.input = ref} active={this.state.chatActive}
							   onPasteFiles={(files) => this.drop.attachFiles(files)} />,
					<MediaDrop ref={(ref: any) => this.drop = ref} active={this.state.chatActive}
							   onAttachHtml={this.onAddText} onAttachFiles={this.onAddAttachment} />,
					<Footer messages={this.state.messages} inQueue={this.state.chatInQueue}
							active={this.state.chatActive} onEmoji={this.onAddText} onAttach={this.onMenuAttach}
							onEndChat={this.onEndChat} onToggleNotification={this.onToggleNotification}
							notificationActive={this.state.notificationActive} />,
				] as any)}
			</div>
		);
	}
}
